import { Col } from 'antd';
import moment from 'moment';
import { FC, useEffect } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { CustomCycle, CycleStatus, NewCustomCycle, StatusColor } from '~src/interfaces/cycle.interface';
import StatusBox from '~src/shared/StatusBox';
import SvgIcon from '~src/shared/SvgIcon';

import { ScheduleI } from '~src/interfaces/schedule.interface';
import { InputLabel } from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/cycleContainer.sc';
import {
  InputLabelContent,
  InputLabelHeader,
} from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/statusSection.sc';
import { addContentTranslation, translateContent } from '~src/services/translationServices';
import { ButtonColorTypes } from '~src/shared/buttons/basicButtons/BaseButton.sc';
import PrimaryButton from '~src/shared/buttons/basicButtons/PrimaryButton.sc';
import CheckBox from '~src/shared/form/Checkbox';
import CustomFormInput from '~src/shared/reactFinalForm/FormInput';
import * as SC from './CustomizedCycles.sc';

interface Props {
  firstCycleStartDate?: string;
  interval?: number;
  cycles: Array<NewCustomCycle | CustomCycle>;
  onChangeCycles: (cycles: Array<CustomCycle | NewCustomCycle>) => void;
  maxCycleCount?: number;
  schedule: ScheduleI;
  onChangeSchedule: (s: Partial<ScheduleI>) => void;
  onConfirm: () => void;
}

const calculateCycles = (startDate: string, days: number, numberOfCycles: number): NewCustomCycle[] => {
  const cycles = [];
  let currentStartDate = moment(startDate);
  for (let i = 0; i < numberOfCycles; i++) {
    const dueDate = moment(currentStartDate).add(days - 1, 'days');

    cycles.push({
      startDate: moment(currentStartDate).format('YYYY-MM-DD'),
      dueDate: dueDate.format('YYYY-MM-DD'),
      cycleStatus: CycleStatus.Scheduled,
    });

    currentStartDate = moment(currentStartDate).add(days, 'days');
  }

  return cycles;
};

export const DisplayCycleStatus = ({ status }: { status: CycleStatus }) => {
  let statusProps: { customColor: StatusColor; customKey: string } = {
    customColor: StatusColor.Blue,
    customKey: `x.surveys.cycle_section.${CycleStatus.Scheduled}`,
  };

  switch (status) {
    case CycleStatus.Active:
      statusProps = {
        customColor: StatusColor.Green,
        customKey: `x.surveys.cycle_section.${CycleStatus.Active}`,
      };
      break;
    case CycleStatus.InActive:
      statusProps = { customColor: StatusColor.Yellow, customKey: 'x.surveys.cycle_section.closed' };
  }

  return (
    <SC.StatusContainer>
      <StatusBox
        active={Boolean(status)}
        customColor={statusProps.customColor}
        customKey={statusProps.customKey}
      />
    </SC.StatusContainer>
  );
};

export const CustomizedCycles: FC<Props> = ({
  firstCycleStartDate,
  interval: days,
  cycles,
  onChangeCycles,
  onChangeSchedule,
  maxCycleCount = 30,
  schedule,
  onConfirm,
}) => {
  const [t] = useTranslation();

  useEffect(() => {
    if (!cycles.length && firstCycleStartDate && days) {
      onChangeCycles(calculateCycles(firstCycleStartDate, days, 1));
    }
  }, []);

  const changeCycle = (index: number, partialCycle: Partial<NewCustomCycle>) => {
    const newCycles = cycles.map((savedCycle, i) => {
      if (index === i) {
        return { ...savedCycle, ...partialCycle };
      }

      return savedCycle;
    });

    onChangeCycles(newCycles);
  };

  const canRemoveCycle = (cycle: CustomCycle | NewCustomCycle) => cycle.cycleStatus === CycleStatus.Scheduled;

  const handleRemoveCycle = (index: number) => () => {
    const newCycles = cycles.filter((_, i) => index !== i);
    onChangeCycles(newCycles);
  };

  const handleRemoveAllCycles = () => {
    onChangeCycles(cycles.filter((cycle) => !canRemoveCycle(cycle)));
  };

  const canAddNewCycle = cycles.length < maxCycleCount;
  const canDisableDeleteCycles = cycles.some((cycle) => cycle.cycleStatus === CycleStatus.Scheduled);

  const handleAddCycle = () => {
    if (!canAddNewCycle) return;
    let startDate = firstCycleStartDate || moment().format('l');

    if (cycles.length > 0) {
      startDate =
        moment(cycles[cycles.length - 1].dueDate)
          .add(1, 'day')
          .format('l') || moment().format('l');
    }

    const newCycle = calculateCycles(startDate, days ?? 1, 1);
    onChangeCycles([...cycles, ...newCycle]);
  };

  const isValidDate = (maybeDate: string) => !new Date(maybeDate).toString().includes('Invalid');
  const startDate = firstCycleStartDate
    ? moment(firstCycleStartDate).toDate()
    : moment().subtract(1, 'day').toDate();

  return (
    <SC.Container>
      <Form
        onSubmit={() => {}}
        initialValues={t}
        enableReinitialize
        keepDirtyOnReinitialize
        render={({ handleSubmit }) => {
          return (
            <form onSubmit={handleSubmit}>
              <>
                {cycles.length > 0 && (
                  <>
                    <SC.CyclesSection>
                      {cycles.map((cycle, index) => {
                        const minStartDate =
                          index === 0 ? startDate : moment(cycles[index - 1].dueDate).toDate();
                        const minDueDate =
                          index === 0
                            ? moment(cycles[0].startDate).toDate()
                            : moment(minStartDate).add(1, 'day').toDate();

                        const isStartValid =
                          cycle.cycleStatus === CycleStatus.InActive ||
                          cycle.cycleStatus === CycleStatus.Active ||
                          moment(cycle.startDate).toDate().getTime() >= minStartDate.getTime();
                        const isDueValid = moment(cycle.dueDate).toDate().getTime() >= minDueDate.getTime();

                        const canEdit = cycle.cycleStatus !== CycleStatus.InActive;
                        const canEditStartDate = canEdit && cycle.cycleStatus !== CycleStatus.Active;
                        const canEditDueDate = canEdit;

                        return (
                          <SC.CyclesContainer key={index}>
                            <Col span={2}>
                              <SC.CyclesLabel data-translation="x.surveys.cycle_section.label">
                                {t('x.surveys.cycle_section.label')}
                              </SC.CyclesLabel>
                            </Col>
                            <Col span={6}>
                              <SC.FormInput
                                label={t('x.custom.cycle_name')}
                                type="text"
                                labelKey="x.custom.cycle_name"
                                onChange={(input) => {
                                  changeCycle(index, {
                                    name: addContentTranslation(input.target.value, cycle.name),
                                  });
                                }}
                                readonly={!canEdit}
                                value={translateContent(cycle.name ?? {})}
                              />
                            </Col>
                            <Col span={5}>
                              <SC.CustomDatePickerStyled
                                error={isStartValid ? '' : 'invalid-start-date'}
                                allowClear={false}
                                dateValue={String(cycle.startDate)}
                                label={t('x.surveys.cycle_section.dates.start')}
                                labelKey="x.surveys.cycle_section.dates.start"
                                onChange={(date) =>
                                  changeCycle(index, { startDate: date?.format('YYYY-MM-DD') })
                                }
                                datePickerOptions={{
                                  disabled: !canEditStartDate,
                                  disabledDate: (d) =>
                                    d.toDate().getTime() < minStartDate.getTime() ||
                                    d.toDate().getTime() <
                                      moment(moment().format('YYYY-MM-DD')).toDate().getTime(),
                                }}
                              />
                            </Col>
                            <Col span={5}>
                              <SC.CustomDatePickerStyled
                                error={isDueValid ? '' : 'invalid-due-date'}
                                allowClear={false}
                                dateValue={
                                  isValidDate(String(cycle.dueDate)) ? String(cycle.dueDate) : cycle.startDate
                                }
                                label={t('x.surveys.cycle_section.dates.due')}
                                labelKey="x.surveys.cycle_section.dates.due"
                                onChange={(date) =>
                                  changeCycle(index, { dueDate: date?.format('YYYY-MM-DD') })
                                }
                                datePickerOptions={{
                                  disabled: !canEditDueDate,
                                  disabledDate: (d) =>
                                    d.toDate().getTime() < minDueDate.getTime() ||
                                    d.toDate().getTime() < moment().toDate().getTime(),
                                }}
                              />
                            </Col>
                            <Col span={4}>
                              <DisplayCycleStatus status={cycle.cycleStatus} />
                            </Col>
                            <Col span={1}>
                              <SC.ActionButtonContainer>
                                <SC.ActionButton
                                  $disabled={!canRemoveCycle(cycle)}
                                  onClick={handleRemoveCycle(index)}
                                >
                                  <SvgIcon width={20} viewBoxWidth={20} viewBoxHeight={22} name="iconCross" />
                                </SC.ActionButton>
                              </SC.ActionButtonContainer>
                            </Col>
                          </SC.CyclesContainer>
                        );
                      })}
                    </SC.CyclesSection>
                    <SC.ToggleInputContainer>
                      <CheckBox
                        id={`${schedule.id}-customCycleMail`}
                        label={t('x.surveys.cycle_section.input.custom_cycle_mail.label')}
                        labelKey={'x.surveys.cycle_section.input.custom_cycle_mail.label'}
                        onChange={(value) => onChangeSchedule({ repetitionMail: value })}
                        isChecked={schedule.repetitionMail}
                        help={t('x.surveys.cycle_section.input.custom_cycle_mail.help')}
                      />
                    </SC.ToggleInputContainer>
                    <SC.ToggleInputContainer>
                      <InputLabel>
                        <InputLabelHeader data-translation="x.surveys.schedule.cycle_section.input.reminder.title">
                          {t('x.surveys.schedule.cycle_section.input.reminder.title')}
                        </InputLabelHeader>
                        <InputLabelContent data-translation="x.surveys.schedule.cycle_section.input.reminder.description">
                          {t('x.surveys.schedule.cycle_section.input.reminder.description')}
                        </InputLabelContent>
                      </InputLabel>
                      <SC.DaysContainer>
                        <CustomFormInput
                          type="number"
                          min={0}
                          label={t('x.surveys.schedule.cycle_section.input.amountdays')}
                          labelKey="x.surveys.schedule.cycle_section.input.amountdays"
                          onChange={(input) => onChangeSchedule({ reminder: Number(input.target.value) })}
                          value={schedule.reminder ?? 0}
                        />
                      </SC.DaysContainer>
                    </SC.ToggleInputContainer>
                  </>
                )}
                <SC.ActionContainer>
                  <PrimaryButton
                    disabled={!canAddNewCycle}
                    data-translation="x.surveys.cycle_section.add.new.cycle"
                    onClick={handleAddCycle}
                  >
                    {t('x.surveys.cycle_section.add.new.cycle')}
                  </PrimaryButton>
                  <SC.RightContainer>
                    <SC.SecondaryButtonStyled
                      $disabled={!canDisableDeleteCycles}
                      data-translation="x.surveys.cycle_section.delete"
                      onClick={handleRemoveAllCycles}
                    >
                      <>{t('x.surveys.cycle_section.delete')}</>
                    </SC.SecondaryButtonStyled>
                    <PrimaryButton
                      $buttonColor={ButtonColorTypes.LightBlue}
                      data-translation="x.surveys.cycle_section.add.confirm"
                      onClick={onConfirm}
                    >
                      {t('x.surveys.cycle_section.add.confirm')}
                    </PrimaryButton>
                  </SC.RightContainer>
                </SC.ActionContainer>
              </>
            </form>
          );
        }}
      />
    </SC.Container>
  );
};
